import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getDepartmentDetails, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowLeft, FaTrash, FaBuilding, FaCalendarAlt, FaInfoCircle, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const DepartmentDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { departmentDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const locale = 'en-US';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const safeText = (value) => {
    if (value === null || value === undefined) return t('departments.notSet');
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : t('departments.notSet');
    }
    return String(value);
  };

  useEffect(() => {
    if (id) {
      dispatch(getDepartmentDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
      toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const handleDelete = () => {
    navigate(`/departments`);
  };

  const handleBack = () => {
    navigate('/departments');
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('departments.loading', { defaultValue: 'Loading...' })}</span>
        </div>
      </div>
    );
  }

  if (!departmentDetails) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '20px' }}></i>
          <h5 style={{ color: 'white', marginBottom: '10px' }}>{t('departments.details.notFound')}</h5>
          <p style={{ color: '#6c757d', marginBottom: '20px' }}>{t('departments.details.notFoundDescription')}</p>
          <button
            onClick={handleBack}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              padding: '12px 25px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto',
              flexDirection: isRTL ? 'row' : 'row-reverse'
            }}
          >
            <FaArrowLeft />
            {t('departments.details.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: 'white',
                textAlign
              }}>
                {t('departments.details.title')}
              </h1>
              <button
                onClick={handleBack}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse'
                }}
              >
                <FaArrowLeft />
                {t('departments.details.back')}
              </button>
            </div>
          </div>

          <div style={{ padding: '30px' }}>
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <FaBuilding style={{ fontSize: '1.2rem', color: '#AC2000', marginInlineStart: isRTL ? '10px' : '0', marginInlineEnd: isRTL ? '0' : '10px' }} />
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px', textAlign }}>{t('departments.details.info.title')}</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px',
                      textAlign
                    }}>
                      {t('departments.details.info.namePrimary')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      textAlign
                    }}>
                      {safeText(departmentDetails.name)}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px',
                      textAlign
                    }}>
                      {t('departments.details.info.nameArabic')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      textAlign,
                      direction: 'rtl'
                    }}>
                      {safeText(departmentDetails.name_ar)}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px',
                      textAlign
                    }}>
                      {t('departments.details.info.nameEnglish')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '16px', 
                      fontWeight: 'bold',
                      textAlign,
                      direction: 'ltr'
                    }}>
                      {safeText(departmentDetails.name_en)}
                    </p>
                  </div>
                  <div>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '5px',
                      color: '#6c757d',
                      fontSize: '14px',
                      textAlign
                    }}>
                      {t('departments.details.info.createdAt')}
                    </label>
                    <p style={{ 
                      margin: 0, 
                      color: 'white', 
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textAlign
                    }}>
                      <FaCalendarAlt />
                      {new Date(departmentDetails.created_at).toLocaleDateString(locale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <FaFileAlt style={{ fontSize: '1.2rem', color: '#17a2b8', marginInlineStart: isRTL ? '10px' : '0', marginInlineEnd: isRTL ? '0' : '10px' }} />
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px', textAlign }}>{t('departments.details.description.title')}</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
                  <div style={{
                    border: '1px solid #2f3547',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#1b3350'
                  }}>
                    <h4 style={{ marginTop: 0, color: '#17a2b8', fontSize: '14px', textAlign }}>{t('departments.details.description.arabic')}</h4>
                    <p style={{
                      margin: 0,
                      color: 'white',
                      lineHeight: '1.6',
                      fontSize: '14px',
                      textAlign,
                      direction: 'rtl'
                    }}>
                      {safeText(departmentDetails.description_ar || departmentDetails.description)}
                    </p>
                  </div>
                  <div style={{
                    border: '1px solid #2f3547',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#1b3350'
                  }}>
                    <h4 style={{ marginTop: 0, color: '#17a2b8', fontSize: '14px', textAlign }}>{t('departments.details.description.english')}</h4>
                <p style={{ 
                  margin: 0, 
                  color: 'white', 
                  lineHeight: '1.6',
                  fontSize: '14px',
                      textAlign,
                      direction: 'ltr'
                }}>
                      {safeText(departmentDetails.description_en || departmentDetails.description)}
                </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div style={{
                backgroundColor: '#1a1f2e',
                borderRadius: '8px',
                padding: '20px',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '18px', textAlign }}>{t('departments.details.actions.title')}</h3>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    onClick={handleDelete}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexDirection: isRTL ? 'row' : 'row-reverse'
                    }}
                  >
                    <FaTrash />
                    {t('departments.details.actions.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDetails;
